import Layout from "@components/Layout"
import RadioCard from "@components/RadioCard"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

interface StaticOption {
  emoji: string
  key: string
}

interface DynamicOption {
  emoji: string
  label: string
  description: string
  value: string
}

interface RadioCardListPageProps {
  translationKey: string
  options: StaticOption[] | DynamicOption[]
  subtitle?: string
  onBeforeSubmit?: (value: string, key: string) => void
  header?: React.ReactNode
}

function isDynamicOptions(
  options: StaticOption[] | DynamicOption[],
): options is DynamicOption[] {
  return options.length > 0 && "label" in options[0]
}

export default function RadioCardListPage({
  translationKey,
  options,
  subtitle,
  onBeforeSubmit,
  header,
}: RadioCardListPageProps) {
  const submit = useSubmit()
  const { t } = useTranslation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onBeforeSubmit) {
      const key = event.currentTarget.dataset.optionKey ?? ""
      onBeforeSubmit(event.currentTarget.value, key)
    }
    submit(event.currentTarget.form)
  }

  return (
    <Layout>
      {header}

      <div className="space-y-2 text-center">
        <h1 className="heading">
          {t(`pages.onboarding.${translationKey}.heading`)}
        </h1>
        {subtitle && (
          <h2 className="text-text-secondary font-semibold">
            {t(`pages.onboarding.${translationKey}.${subtitle}`)}
          </h2>
        )}
      </div>

      <Form className="custom-form" method="post">
        <div className="flex grow flex-col justify-center space-y-4">
          {isDynamicOptions(options)
            ? options.map((option) => (
                <RadioCard
                  key={option.value}
                  emoji={option.emoji}
                  label={option.label}
                  description={option.description}
                  name={t(`pages.onboarding.${translationKey}.heading`, {
                    lng: "en",
                  })}
                  onChange={handleChange}
                  value={option.value}
                />
              ))
            : options.map((option) => (
                <RadioCard
                  key={option.key}
                  emoji={option.emoji}
                  label={t(`pages.onboarding.${translationKey}.${option.key}`)}
                  description={t(
                    `pages.onboarding.${translationKey}.${option.key}Description`,
                  )}
                  name={t(`pages.onboarding.${translationKey}.heading`, {
                    lng: "en",
                  })}
                  onChange={handleChange}
                  value={t(`pages.onboarding.${translationKey}.${option.key}`, {
                    lng: "en",
                  })}
                  optionKey={option.key}
                />
              ))}
        </div>
      </Form>
    </Layout>
  )
}
